import { useEffect, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { Button } from '@actual-app/components/button';
import { AnimatedLoading } from '@actual-app/components/icons/AnimatedLoading';
import { theme } from '@actual-app/components/theme';
import { View } from '@actual-app/components/view';
import type { AccountEntity } from '@actual-app/core/types/models';

import type {
  FinancialManagementRequest,
  FinancialManagementState,
  FinancialOnboardingState,
  ProductFinanceClient,
} from '#components/product-finance/client';
import { FinancialOnboarding } from '#components/product-finance/FinancialOnboarding';
import { PRODUCT_FINANCE_NAMESPACE } from '#components/product-finance/product-i18n';
import { ProductLanguageSettings } from '#components/product-finance/ProductLanguageSettings';

import { CommitmentManagement } from './CommitmentManagement';
import { CycleManagement } from './CycleManagement';
import { GoalManagement } from './GoalManagement';
import { IncomeDistributionRules } from './IncomeDistributionRules';
import { IncomeManagement } from './IncomeManagement';
import { ManagementCard } from './management-shared';

export type ManagementSection =
  | 'general'
  | 'accounts'
  | 'incomes'
  | 'cycles'
  | 'commitments'
  | 'goals';

export type FinancialManagementProps = Readonly<{
  onboarding: FinancialOnboardingState;
  accounts: readonly AccountEntity[];
  client: ProductFinanceClient;
  request: FinancialManagementRequest;
  onClose: () => Promise<void>;
}>;

export const SECTIONS: readonly Readonly<{
  id: ManagementSection;
  label:
    | 'General'
    | 'Accounts'
    | 'Incomes'
    | 'Cycles'
    | 'Fixed expenses'
    | 'Goals';
}>[] = [
  { id: 'general', label: 'General' },
  { id: 'accounts', label: 'Accounts' },
  { id: 'incomes', label: 'Incomes' },
  { id: 'cycles', label: 'Cycles' },
  { id: 'commitments', label: 'Fixed expenses' },
  { id: 'goals', label: 'Goals' },
];

export function FinancialManagement({
  onboarding,
  accounts,
  client,
  request,
  onClose,
}: FinancialManagementProps) {
  const { t } = useTranslation(PRODUCT_FINANCE_NAMESPACE);
  const [activeSection, setActiveSection] =
    useState<ManagementSection>('general');
  const [state, setState] = useState<FinancialManagementState | null>(null);
  const [loadError, setLoadError] = useState(false);

  async function refresh() {
    try {
      setState(await client.loadFinancialManagement(request));
      setLoadError(false);
    } catch {
      setLoadError(true);
    }
  }

  useEffect(() => {
    void client
      .loadFinancialManagement(request)
      .then(nextState => {
        setState(nextState);
        setLoadError(false);
      })
      .catch(() => setLoadError(true));
  }, [client, request]);

  return (
    <View style={{ width: '100%', gap: 14, flexShrink: 0 }}>
      <View
        style={{
          flexDirection: 'row',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          gap: 12,
        }}
      >
        <View>
          <h1 style={{ margin: 0, fontSize: 25 }}>
            <Trans t={t}>Product finance settings</Trans>
          </h1>
          <p style={{ marginBottom: 0, color: theme.pageTextSubdued }}>
            <Trans t={t}>
              Manage the sources that drive your financial dashboard.
            </Trans>
          </p>
        </View>
        <Button onPress={() => void onClose()} style={{ minHeight: 42 }}>
          <Trans t={t}>Back to dashboard</Trans>
        </Button>
      </View>

      <nav aria-label={t('Product finance settings')}>
        <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: 6 }}>
          {SECTIONS.map(section => (
            <Button
              key={section.id}
              {...(activeSection === section.id
                ? { variant: 'primary' as const }
                : {})}
              onPress={() => setActiveSection(section.id)}
              style={{ minHeight: 42 }}
            >
              {t(section.label)}
            </Button>
          ))}
        </View>
      </nav>

      {loadError && (
        <ManagementCard title={<Trans>Settings unavailable</Trans>}>
          <p role="alert">
            <Trans>
              Your budget data was not changed. Try loading these settings
              again.
            </Trans>
          </p>
          <Button variant="primary" onPress={() => void refresh()}>
            <Trans>Try again</Trans>
          </Button>
        </ManagementCard>
      )}
      {!loadError && state === null && (
        <output style={{ padding: 30, textAlign: 'center' }}>
          <AnimatedLoading style={{ width: 24, height: 24 }} />
          <span style={{ display: 'block', marginTop: 8 }}>
            <Trans>Loading financial settings…</Trans>
          </span>
        </output>
      )}
      {state !== null && activeSection === 'general' && (
        <View style={{ gap: 12 }}>
          <ManagementCard title={<Trans>Language</Trans>}>
            <ProductLanguageSettings />
          </ManagementCard>
          <FinancialOnboarding
            initialState={onboarding}
            accounts={accounts}
            client={client}
            mode="edit"
            editStep="settings"
            onComplete={refresh}
          />
        </View>
      )}
      {state !== null && activeSection === 'accounts' && (
        <FinancialOnboarding
          initialState={onboarding}
          accounts={accounts}
          client={client}
          mode="edit"
          editStep="accounts"
          onComplete={refresh}
        />
      )}
      {state !== null && activeSection === 'incomes' && (
        <View style={{ gap: 12 }}>
          <IncomeManagement state={state} client={client} onChanged={refresh} />
          <IncomeDistributionRules state={state} client={client} />
        </View>
      )}
      {state !== null && activeSection === 'cycles' && (
        <CycleManagement state={state} client={client} onChanged={refresh} />
      )}
      {state !== null && activeSection === 'commitments' && (
        <CommitmentManagement
          state={state}
          client={client}
          onChanged={refresh}
        />
      )}
      {state !== null && activeSection === 'goals' && (
        <GoalManagement state={state} client={client} onChanged={refresh} />
      )}
    </View>
  );
}
